import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api, useCategories } from "@/lib/api";
import { ProductGrid } from "@/components/ProductCard";
import { CatalogFilters } from "@/components/CatalogFilters";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const SORTS = [["featured", "In evidenza"], ["price_asc", "Prezzo crescente"], ["price_desc", "Prezzo decrescente"], ["discount", "Sconto maggiore"], ["name", "Nome A-Z"]];

export default function Catalog() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const cats = useCategories() || [];
  const cat = cats.find((c) => c.slug === slug);
  const q = sp.get("q") || "";
  const page = Number(sp.get("page") || 1);
  const key = `${slug}|${sp.toString()}`;

  useEffect(() => {
    setData(null);
    const params = { category: slug, q: sp.get("q") || undefined, sort: sp.get("sort") || "featured", page: Number(sp.get("page") || 1),
      offers: sp.get("offerte") === "1" || undefined, min_price: sp.get("min") || undefined, max_price: sp.get("max") || undefined };
    api.get("/products", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 1 }));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!("page" in patch)) next.delete("page");
    setSp(next);
  };
  if (slug && cats.length && !cat) return <div className="py-32 text-center text-slate-500" data-testid="category-not-found">Categoria non trovata.</div>;
  const title = cat?.name || (sp.get("offerte") === "1" ? "Offerte" : q ? `Risultati per “${q}”` : "Tutti i prodotti");

  return (
    <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6" data-testid="catalog-page">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0066FF]">{cat?.parent_path || "Catalogo"}</p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-3xl font-bold tracking-[-0.03em] text-[#0A0B0E] sm:text-4xl" data-testid="catalog-title">{title}</h1>
        <span className="font-mono text-xs text-slate-500" data-testid="catalog-count">{data ? `${data.total} prodotti` : "…"}</span>
      </div>
      <div className="mt-8 flex items-center gap-3 lg:hidden">
        <button onClick={() => setFiltersOpen(true)} data-testid="mobile-filters-btn" className="flex h-10 items-center gap-2 rounded-full border border-slate-300 px-4 text-sm font-medium text-slate-900">
          <SlidersHorizontal className="h-4 w-4" /> Filtri
        </button>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block"><CatalogFilters sp={sp} update={update} cats={cats} slug={slug} sorts={SORTS} /></aside>
        <div className="min-w-0">
          {data && !data.items.length ? (
            <div className="rounded-3xl border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="catalog-empty">Nessun prodotto trovato con questi filtri.</div>
          ) : (
            <ProductGrid items={data?.items || []} loading={!data} testid="catalog-grid" />
          )}
          {data && data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-4 text-sm">
              <button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} className="rounded-full border border-slate-300 px-5 py-2 text-slate-900 disabled:opacity-30" data-testid="catalog-prev-page">Precedente</button>
              <span className="font-mono text-xs text-slate-400">{page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => update({ page: String(page + 1) })} className="rounded-full border border-slate-300 px-5 py-2 text-slate-900 disabled:opacity-30" data-testid="catalog-next-page">Successiva</button>
            </div>
          )}
        </div>
      </div>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto border-slate-200 bg-white text-slate-900">
          <SheetHeader><SheetTitle className="text-left text-slate-900">Filtri</SheetTitle></SheetHeader>
          <div className="mt-6"><CatalogFilters sp={sp} update={update} cats={cats} slug={slug} sorts={SORTS} onPick={() => setFiltersOpen(false)} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
